import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Check, ExternalLink, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useBillingMutations } from "@/hooks/billing";
import { useHasPermission } from "@/hooks/permissions";

/**
 * Lightweight entitlement check for use on the public /pricing route.
 * Only runs when a user is authenticated. Returns enforcement_state.
 */
function usePricingEntitlement(companyId: string | undefined) {
  return useQuery({
    queryKey: ['pricing-entitlement', companyId],
    queryFn: async () => {
      await supabase.rpc('ensure_user_context');
      const { data, error } = await supabase.rpc('get_company_entitlement');
      if (error) throw error;
      return data as { enforcement_state: string; plan_name: string | null; source: string };
    },
    enabled: !!companyId,
    staleTime: 60_000,
  });
}

const pricingPlans = [
  {
    name: "Pro Plan",
    slug: "pro",
    price: "€15",
    period: "per month",
    description: "Complete workforce management solution",
    features: [
      "3-month free trial — no charge until trial ends",
      "5 employees included",
      "€1/month per additional employee",
      "Time tracking & attendance",
      "Leave management",
      "Shift scheduling",
      "Payroll reports",
      "Team management",
      "Email support",
    ],
    popular: true,
  },
];

export default function Pricing() {
  const [loading, setLoading] = useState<string | null>(null);
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const { createPortalSession } = useBillingMutations();
  const canManageBilling = useHasPermission('system_config_manage');

  const { data: entitlement } = usePricingEntitlement(profile?.company_id);
  const enforcementState = entitlement?.enforcement_state ?? null;
  const entitlementSource = entitlement?.source ?? null;
  const isAlreadySubscribed = (enforcementState === 'full_access' || enforcementState === 'grace_period') && entitlementSource === 'stripe';
  const isOverrideAccess = (enforcementState === 'full_access') && entitlementSource === 'override';

  const handleSubscribe = async (planSlug: string) => {
    if (!user) {
      navigate("/auth");
      return;
    }

    setLoading(planSlug);
    
    try {
      const { data, error } = await supabase.functions.invoke("create-checkout-session", {
        body: { plan: planSlug },
      });

      if (error) throw error;

      // Handle 409 — already subscribed (backend-authoritative guard)
      if (data?.code === 'already_subscribed') {
        toast.info("You already have an active subscription.");
        navigate("/business-settings/billing");
        return;
      }

      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error: any) {
      console.error("Error creating checkout session:", error);
      toast.error("Failed to create checkout session. Please try again.");
    } finally {
      setLoading(null);
    }
  };

  const handleManageSubscription = () => {
    createPortalSession.mutate();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-secondary/20">
      <div className="container mx-auto px-4 py-16">
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold mb-4">
            {isAlreadySubscribed ? "Your Plan" : isOverrideAccess ? "Plans" : "Choose Your Plan"}
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            {isAlreadySubscribed
              ? "You're currently subscribed. Manage your plan from here."
              : isOverrideAccess
                ? `You currently have access via ${entitlement?.plan_name || 'an internal grant'}.`
                : "Streamline your workforce management with our comprehensive HR solution"
            }
          </p>
        </div>

        {user && (
          <div className="text-center mb-8">
            <Link 
              to="/business-settings/billing" 
              className="inline-flex items-center text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              « Back to Billing Settings
            </Link>
          </div>
        )}

        <div className={`grid gap-8 mx-auto ${
          pricingPlans.length === 1 
            ? 'grid-cols-1 max-w-md' 
            : 'md:grid-cols-2 max-w-4xl'
        }`}>
          {pricingPlans.map((plan) => (
            <Card 
              key={plan.name} 
              className={`relative ${plan.popular ? 'border-primary shadow-lg' : ''}`}
            >
              {isAlreadySubscribed ? (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-primary text-primary-foreground text-sm font-medium px-3 py-1 rounded-full">
                    Current Plan
                  </span>
                </div>
              ) : plan.popular ? (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-primary text-primary-foreground text-sm font-medium px-3 py-1 rounded-full">
                    Most Popular
                  </span>
                </div>
              ) : null}
              
              <CardHeader>
                <CardTitle className="text-2xl">{plan.name}</CardTitle>
                <CardDescription>{plan.description}</CardDescription>
                <div className="flex items-baseline space-x-2">
                  <span className="text-3xl font-bold">{plan.price}</span>
                  <span className="text-muted-foreground">{plan.period}</span>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  Base plan includes 5 employees. Additional employees €1/month each.
                </p>
                <p className="text-sm font-semibold text-primary mt-2">
                  3-month free trial · No charge until trial ends
                </p>
              </CardHeader>
              
              <CardContent>
                <ul className="space-y-3">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-center">
                      <Check className="h-4 w-4 text-primary mr-3 flex-shrink-0" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
              
              <CardFooter>
                {isAlreadySubscribed ? (
                  <Button 
                    className="w-full" 
                    size="lg"
                    onClick={handleManageSubscription}
                    disabled={createPortalSession.isPending}
                    variant="outline"
                  >
                    {createPortalSession.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Loading...
                      </>
                    ) : (
                      <>
                        <ExternalLink className="mr-2 h-4 w-4" />
                        Manage Subscription
                      </>
                    )}
                  </Button>
                ) : isOverrideAccess ? (
                  <Button 
                    className="w-full" 
                    size="lg"
                    onClick={() => navigate('/business-settings/billing')}
                    variant="outline"
                  >
                    View Billing Settings
                  </Button>
                ) : (
                  user && !canManageBilling ? (
                    <p className="text-sm text-muted-foreground text-center w-full">
                      Contact your administrator to manage billing.
                    </p>
                  ) : (
                  <Button 
                    className="w-full" 
                    size="lg"
                    onClick={() => handleSubscribe(plan.slug)}
                    disabled={loading === plan.slug}
                    variant={plan.popular ? "default" : "outline"}
                  >
                    {loading === plan.slug 
                      ? "Processing..." 
                      : user 
                        ? "Start Free Trial" 
                        : "Start Free Trial"
                    }
                  </Button>
                  )
                )}
              </CardFooter>
            </Card>
          ))}
        </div>

        <div className="text-center mt-16">
          <p className="text-muted-foreground">
            Cancel anytime. Manage your subscription from your billing settings.
          </p>
        </div>
      </div>
    </div>
  );
}
